import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useRolodexIds } from 'features/fde'
import { postFetcher, RolodexEntryQueryBody } from 'features/rolodex'
import { useCallback, useMemo } from 'react'
import useSWR, { Cache, SWRConfiguration, useSWRConfig } from 'swr'
import { RolodexLoadedEntryJson } from 'types'

const ENTRY_CACHE_KEY_PREFIX = 'cached-by-id-rolodex-entry'

function createEntryCacheKey(id: string) {
  return `${ENTRY_CACHE_KEY_PREFIX}-${id}`
}

function partitionByCache<T extends RolodexLoadedEntryJson>(
  cache: Cache,
  ids: readonly string[]
): { cached: T[]; uncachedIds: string[] } {
  const cached: T[] = []
  const uncachedIds: string[] = []

  for (const id of ids) {
    const cacheKey = createEntryCacheKey(id)
    const cacheEntry = cache.get(cacheKey)
    if (cacheEntry?.data) {
      cached.push(cacheEntry.data as T)
    } else {
      uncachedIds.push(id)
    }
  }

  return { cached, uncachedIds }
}

function mergeEntriesInOrder<T extends RolodexLoadedEntryJson>(
  ids: readonly string[],
  cached: T[],
  fetched: T[]
): T[] {
  const entriesById = new Map<string, T>()

  for (const entry of cached) {
    entriesById.set(entry.entry.id, entry)
  }
  for (const entry of fetched) {
    entriesById.set(entry.entry.id, entry)
  }

  return ids
    .map((id) => entriesById.get(id))
    .filter((entry): entry is T => entry !== undefined)
}

export type UseRolodexEntriesCachedByIdReturn<T> = {
  data: T[] | undefined
  error: Error | null
  isLoading: boolean
  isValidating: boolean
  mutate: () => Promise<void>
}

export function useOnSuccessCacheEntriesById<
  T extends RolodexLoadedEntryJson,
>(): (data: T[]) => void {
  const { mutate: swrMutate } = useSWRConfig()

  return useCallback(
    (entries: T[]) => {
      for (const entry of entries) {
        const cacheKey = createEntryCacheKey(entry.entry.id)
        swrMutate(cacheKey, entry, { revalidate: false })
      }
    },
    [swrMutate]
  )
}

export function useRolodexEntriesCachedById<T extends RolodexLoadedEntryJson>(
  ids: readonly string[],
  swrConfig?: SWRConfiguration
): UseRolodexEntriesCachedByIdReturn<T> {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { ROLODEX_URL } = useRuntimeVariables()
  const { cache } = useSWRConfig()
  const cacheEntriesById = useOnSuccessCacheEntriesById<T>()

  const endpoint = `${ROLODEX_URL}/api/v2/entries/load`

  const fetcher = useMemo(() => {
    return async (): Promise<T[]> => {
      if (!ids || ids.length === 0) return []

      const { cached: cachedEntries, uncachedIds } = partitionByCache<T>(
        cache,
        ids
      )

      if (uncachedIds.length === 0) {
        return mergeEntriesInOrder(ids, cachedEntries, [])
      }

      const body = JSON.stringify({
        ids: uncachedIds,
        limit: uncachedIds.length,
      })
      const fetchedEntries: T[] = await postFetcher(endpoint, body, axios)

      cacheEntriesById(fetchedEntries)

      return mergeEntriesInOrder(ids, cachedEntries, fetchedEntries)
    }
  }, [ids, endpoint, axios, cache, cacheEntriesById])

  const batchFetchEntriesSWRKey = useMemo(() => {
    if (!ids || ids.length === 0) return null
    return ['batch-fetch-rolodex-entries', [...ids], endpoint]
  }, [ids, endpoint])

  const {
    data: entries,
    isLoading,
    isValidating,
    error,
    mutate: mutateEntries,
  } = useSWR<T[]>(batchFetchEntriesSWRKey, fetcher, swrConfig)

  const mutate = useCallback(async () => {
    await mutateEntries()
  }, [mutateEntries])

  return {
    data: entries,
    error: error ?? null,
    isLoading,
    isValidating,
    mutate,
  }
}

export function useRolodexEntriesTwoPhaseFetchCachedById<
  T extends RolodexLoadedEntryJson,
>(
  loadOptions: RolodexEntryQueryBody,
  swrConfig?: SWRConfiguration
): UseRolodexEntriesCachedByIdReturn<T> {
  const {
    ids,
    isLoading: isLoadingIds,
    isValidating: isValidatingIds,
    error: idsError,
    forceRefresh: refreshIds,
  } = useRolodexIds(loadOptions)

  const {
    data,
    isLoading: isLoadingEntries,
    isValidating: isValidatingEntries,
    error: entriesError,
    mutate: mutateEntries,
  } = useRolodexEntriesCachedById<T>(ids ?? [], swrConfig)

  const mutate = useCallback(async () => {
    await refreshIds()
    await mutateEntries()
  }, [refreshIds, mutateEntries])

  return {
    data,
    error: idsError ?? entriesError ?? null,
    isLoading: isLoadingIds || isLoadingEntries,
    isValidating: isValidatingIds || isValidatingEntries,
    mutate,
  }
}
